import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Repeat, Play, Pause, Trash2, Loader2, Calendar,
  CheckCircle2, Clock, AlertTriangle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { HelpPanel } from '../components/ui/help-panel';
import { recurringEntriesHelp } from '../lib/helpContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Bi-Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export default function RecurringEntries() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  const wsId = currentWorkspace?.workspace_id;

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    try {
      const [recRes, acctRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/recurring-entries?include_inactive=true`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/accounts`),
      ]);
      if (recRes.ok) setEntries(await recRes.json());
      if (acctRes.ok) { const data = await acctRes.json(); setAccounts(data.accounts || data || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGenerate = async (recId) => {
    setGenerating(recId);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/recurring-entries/${recId}/generate`, { method: 'POST' });
      if (res.ok) { const data = await res.json(); toast.success(data.message); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setGenerating(null); }
  };

  const toggleActive = async (recId, active) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/recurring-entries/${recId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !active }),
    });
    fetchAll();
  };

  const handleDelete = async (recId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/recurring-entries/${recId}`, { method: 'DELETE' });
    fetchAll(); toast.success('Deleted');
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64" /></div>;

  const today = new Date().toISOString().split('T')[0];
  const dueEntries = entries.filter(e => e.active && e.next_date && e.next_date <= today);

  return (
    <div className="space-y-6" data-testid="recurring-entries">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Recurring Journal Entries</h1>
          <p className="text-muted-foreground text-sm">Automate repeating journal entries on a schedule</p>
        </div>
        <Button onClick={() => navigate('/recurring-entries/new')} data-testid="new-recurring-btn">
          <Plus className="w-4 h-4 mr-1" /> New Recurring Entry
        </Button>
        <HelpPanel {...recurringEntriesHelp} />
      </div>

      {/* Due Alert */}
      {dueEntries.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">{dueEntries.length} recurring entr{dueEntries.length === 1 ? 'y' : 'ies'} due</p>
              <p className="text-xs text-muted-foreground">Click "Generate" to create the journal entries</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead>Name</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Next Date</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center">Generated</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length > 0 ? entries.map(entry => {
                const isDue = entry.active && entry.next_date && entry.next_date <= today;
                const total = entry.lines?.reduce((s, l) => s + (l.debit || 0), 0) || 0;
                return (
                  <TableRow key={entry.recurring_id} className={cn(!entry.active && "opacity-50")} data-testid={`rec-${entry.recurring_id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{entry.name}</p>
                      {entry.memo && <p className="text-xs text-muted-foreground">{entry.memo}</p>}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{FREQ_LABELS[entry.frequency] || entry.frequency}</Badge></TableCell>
                    <TableCell>
                      <span className={cn("text-sm", isDue ? "text-amber-600 font-medium" : "")}>
                        {entry.next_date}{isDue && ' (Due!)'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("text-[9px]", entry.auto_post ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700")}>
                        {entry.auto_post ? 'Auto-Post' : 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(total)}</TableCell>
                    <TableCell className="text-center text-sm">{entry.entries_created || 0}</TableCell>
                    <TableCell>
                      <Badge className={cn("text-[9px]", entry.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500")}>
                        {entry.active ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {entry.active && (
                          <Button size="sm" variant="outline" onClick={() => handleGenerate(entry.recurring_id)} disabled={generating === entry.recurring_id} data-testid={`generate-${entry.recurring_id}`}>
                            {generating === entry.recurring_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                            Generate
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(entry.recurring_id, entry.active)}>
                          {entry.active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(entry.recurring_id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow><TableCell colSpan={8} className="text-center py-8">
                  <Repeat className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No recurring entries</p>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
